import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Shield, BarChart3, UtensilsCrossed,
  Package, TrendingUp, CheckCircle, DollarSign, Save, Settings, CalendarDays, Award,
  Plus, Pencil, Trash2, Upload, Eye, EyeOff, Layers, Image as ImageIcon, X,
  Clock, Power, PowerOff, AlertTriangle, Mail, MessageSquare, Bell, Store, CreditCard,
  ChevronDown, Check, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND_COLORS } from "@/lib/menuData";
import Footer from "@/components/Footer";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import OrderChatPanel from "@/components/OrderChatPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";

// Helper to format time for display
const formatTime12h = (time24) => {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
};

// Helper to format minutes to "Xh Xm"
const formatMinutes = (mins) => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="bg-white border-slate-100">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div>
          <p className="text-xs text-slate-500 font-medium">{label}</p>
          <p className="text-xl font-extrabold" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function formatDate(date) {
  if (!date) return "";
  return date.toISOString().split("T")[0];
}

const CONCEPT_OPTIONS = [
  { value: "chicken", label: "Krispy Krunchy Chicken", color: BRAND_COLORS.sunsetOrange },
  { value: "pizza", label: "Hunt Brothers Pizza", color: BRAND_COLORS.deepTeal },
  { value: "lunch", label: "Island Grill & Deli", color: "#2E7D32" },
];

// Helper function for duplicate detection
const normalizeForDuplicateCheck = (name) => {
  if (!name) return '';
  let n = name.toLowerCase().trim();
  n = n.replace(/\bbbq\b/g, 'barbecue');
  n = n.replace(/\bbarbeque\b/g, 'barbecue');
  n = n.replace(/\bbar-b-q\b/g, 'barbecue');
  n = n.replace(/[^a-z0-9\s]/g, '');
  n = n.replace(/\s+/g, ' ').trim();
  return n;
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("dashboard");
  // Messages inbox (Phase 15)
  const [messageOrders, setMessageOrders] = useState([]);
  const [chatOrderId, setChatOrderId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [sides, setSides] = useState([]);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingPrices, setEditingPrices] = useState({});
  const [printerIp, setPrinterIp] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [loading, setLoading] = useState(true);

  // CMS State
  const [itemDialog, setItemDialog] = useState({ open: false, mode: "create", item: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: "create", category: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: "" });
  const [hasUnpublished, setHasUnpublished] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Inventory State (database-connected)
  const [pizzaToppings, setPizzaToppings] = useState([]);
  const [wingSauces, setWingSauces] = useState([]);
  const [dippingSauces, setDippingSauces] = useState([]);
  const [extraSides, setExtraSides] = useState([]);
  const [lunchSides, setLunchSides] = useState([]);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [inventoryDialog, setInventoryDialog] = useState({ open: false, type: null, mode: "create", item: null });
  const [inventoryForm, setInventoryForm] = useState({ name: "", category: "meats", price: "", description: "", available: true, sort_order: 0 });

  // Concept Hours State
  const [concepts, setConcepts] = useState([]);
  const [conceptDialog, setConceptDialog] = useState({ open: false, concept: null });
  const [restaurantSettings, setRestaurantSettings] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [storePaymentEnabled, setStorePaymentEnabled] = useState(true);
  const [paymentsSaving, setPaymentsSaving] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState({});
  const [welcomePopupEnabled, setWelcomePopupEnabled] = useState(true);
  
  // In-Store Mode Settings - Multi-Employee System
  const [instoreEnabled, setInstoreEnabled] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [instoreSaving, setInstoreSaving] = useState(false);
  const [employeeDialog, setEmployeeDialog] = useState({ open: false, mode: "create", employee: null });
  const [employeeForm, setEmployeeForm] = useState({ name: "", pin: "", role: "Cashier" });

  // Bulk Price Update
  const [bulkPriceDialog, setBulkPriceDialog] = useState(false);
  const [bulkPriceForm, setBulkPriceForm] = useState({ concept: "chicken", action: "increase_pct", value: "" });
  const [bulkPriceSaving, setBulkPriceSaving] = useState(false);

  // Kitchen Availability State
  const [biscuitAvailable, setBiscuitAvailable] = useState(true);
  const [crustRegularAvailable, setCrustRegularAvailable] = useState(true);
  const [crustThinAvailable, setCrustThinAvailable] = useState(true);
  const [pizzaLoyaltyEnabled, setPizzaLoyaltyEnabled] = useState(true);
  const [kitchenSaving, setKitchenSaving] = useState(false);

  // Form state for item dialog
  const [itemForm, setItemForm] = useState({
    concept: "chicken", category: "", name: "", size: "", price: "",
    description: "", image: "", requires_piece_selection: false, available: true,
  });

  // Form state for category dialog
  const [categoryForm, setCategoryForm] = useState({
    concept: "chicken", name: "", key: "", published: true, sort_order: 0,
  });

  const fileInputRef = useRef(null);
  const { token, logout, getAuthHeaders, role } = useAuth();
  const navigate = useNavigate();

  // Phase 15: Messages inbox — fetch unread-messages summary when the Messages tab is opened + poll
  useEffect(() => {
    if (tab !== "messages") return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const [summaryRes, ordersRes] = await Promise.all([
          axios.get(`${API}/admin/messages/unread-summary`, { headers: getAuthHeaders() }),
          axios.get(`${API}/orders?limit=50`, { headers: getAuthHeaders() }),
        ]);
        if (cancelled) return;
        const nameById = new Map(ordersRes.data.map((o) => [o.id, o.customer_name]));
        setMessageOrders(
          (summaryRes.data || []).map((m) => ({ ...m, customer_name: nameById.get(m.order_id) || null }))
        );
      } catch {
        /* ignore */
      }
    };
    load();
    const interval = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [tab, getAuthHeaders]);

  const fetchAnalytics = useCallback(async (startDate, endDate) => {
    try {
      const headers = getAuthHeaders();
      let url = `${API}/admin/analytics`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      const res = await axios.get(url, { headers });
      setAnalytics(res.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { logout(); navigate("/admin/login"); }
    }
  }, [getAuthHeaders, logout, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [sidesRes, ordersRes, menuRes, settingsRes, catsRes, toppingsRes, wingSaucesRes, dippingSaucesRes, extraSidesRes, lunchSidesRes, summaryRes, conceptsRes, restSettingsRes, paymentSettingsRes, promoCodesRes, welcomePopupRes, employeeSettingsRes, biscuitRes, crustRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/admin/sides`, { headers }),
        axios.get(`${API}/orders?limit=50`, { headers }),
        axios.get(`${API}/admin/menu`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }),
        axios.get(`${API}/admin/categories`, { headers }),
        axios.get(`${API}/admin/inventory/pizza-toppings`, { headers }),
        axios.get(`${API}/admin/inventory/wing-sauces`, { headers }),
        axios.get(`${API}/admin/inventory/dipping-sauces`, { headers }),
        axios.get(`${API}/admin/inventory/extra-sides`, { headers }),
        axios.get(`${API}/admin/inventory/lunch-sides`, { headers }),
        axios.get(`${API}/admin/inventory/summary`, { headers }),
        axios.get(`${API}/admin/concepts`, { headers }),
        axios.get(`${API}/admin/restaurant-settings`, { headers }),
        axios.get(`${API}/admin/payment-settings`, { headers }),
        axios.get(`${API}/admin/promo-codes`, { headers }),
        axios.get(`${API}/admin/welcome-popup-settings`, { headers }),
        axios.get(`${API}/admin/employee-settings`, { headers }),
        axios.get(`${API}/settings/biscuit-availability`),
        axios.get(`${API}/settings/crust-availability`),
        axios.get(`${API}/settings/pizza-loyalty`),
      ]);
      setSides(sidesRes.data);
      setOrders(ordersRes.data);
      const items = menuRes.data.items || menuRes.data;
      setMenuItems(Array.isArray(items) ? items : []);
      setCategories(catsRes.data);
      setPizzaToppings(toppingsRes.data || []);
      setWingSauces(wingSaucesRes.data || []);
      setDippingSauces(dippingSaucesRes.data || []);
      setExtraSides(extraSidesRes.data || []);
      setLunchSides(lunchSidesRes.data || []);
      setInventorySummary(summaryRes.data || null);
      setConcepts(conceptsRes.data || []);
      setRestaurantSettings(restSettingsRes.data || {});
      setPaymentsEnabled(paymentSettingsRes.data?.payments_enabled || false);
      setStorePaymentEnabled(paymentSettingsRes.data?.store_payment_enabled !== false);
      setPromoCodes(promoCodesRes.data || []);
      setWelcomePopupEnabled(welcomePopupRes.data?.welcome_popup_enabled !== false);
      setInstoreEnabled(employeeSettingsRes.data?.instore_enabled !== false);
      setEmployees(employeeSettingsRes.data?.employees || []);
      // Kitchen availability settings
      setBiscuitAvailable(biscuitRes.data?.available !== false);
      setCrustRegularAvailable(crustRes.data?.regular !== false);
      setCrustThinAvailable(crustRes.data?.thin !== false);
      setPizzaLoyaltyEnabled(loyaltyRes.data?.enabled !== false);
      const pip = settingsRes.data.find((s) => s.key === "printer_ip");
      if (pip) setPrinterIp(pip.value || "");
      // Check for unpublished changes
      const unpublishedItems = (Array.isArray(items) ? items : []).filter(i => i.published === false);
      const unpublishedCats = catsRes.data.filter(c => c.published === false);
      setHasUnpublished(unpublishedItems.length > 0 || unpublishedCats.length > 0);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { logout(); navigate("/admin/login"); }
    } finally { setLoading(false); }
  }, [getAuthHeaders, logout, navigate]);

  useEffect(() => {
    if (!token || role !== "admin") { navigate("/admin/login"); return; }
    fetchData();
    fetchAnalytics(dateRange.from ? formatDate(dateRange.from) : null, dateRange.to ? formatDate(dateRange.to) : null);
  }, [token, role, navigate, fetchData, fetchAnalytics, dateRange]);

  const handleToggleSide = async (sideId) => {
    try {
      const res = await axios.put(`${API}/admin/sides/${sideId}/toggle`, {}, { headers: getAuthHeaders() });
      setSides((prev) => prev.map((s) => (s.id === sideId ? res.data : s)));
      toast.success(`${res.data.name} ${res.data.available ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to toggle side"); }
  };

  // ==================== Inventory CRUD Handlers ====================
  const getInventoryEndpoint = (type) => {
    const endpoints = {
      "pizza-topping": "pizza-toppings",
      "wing-sauce": "wing-sauces", 
      "dipping-sauce": "dipping-sauces",
      "extra-side": "extra-sides",
      "lunch-side": "lunch-sides",
    };
    return `${API}/admin/inventory/${endpoints[type]}`;
  };

  const getInventoryState = (type) => {
    const states = {
      "pizza-topping": [pizzaToppings, setPizzaToppings],
      "wing-sauce": [wingSauces, setWingSauces],
      "dipping-sauce": [dippingSauces, setDippingSauces],
      "extra-side": [extraSides, setExtraSides],
      "lunch-side": [lunchSides, setLunchSides],
    };
    return states[type];
  };

  const openInventoryDialog = (type, mode = "create", item = null) => {
    setInventoryDialog({ open: true, type, mode, item });
    if (mode === "edit" && item) {
      setInventoryForm({
        name: item.name || "",
        category: item.category || "meats",
        price: item.price?.toString() || "",
        description: item.description || "",
        available: item.available !== false,
        sort_order: item.sort_order || 0,
      });
    } else {
      setInventoryForm({ name: "", category: "meats", price: "", description: "", available: true, sort_order: 0 });
    }
  };

  const handleToggleInventoryItem = async (type, itemId, currentAvailable) => {
    try {
      const [items, setItems] = getInventoryState(type);
      const item = items.find(i => i.id === itemId);
      if (!item) return;
      
      const payload = { name: item.name, available: !currentAvailable, sort_order: item.sort_order || 0 };
      if (type === "pizza-topping") payload.category = item.category;
      if (type === "dipping-sauce" || type === "extra-side") payload.price = item.price || 0;
      if (type === "extra-side") payload.description = item.description || "";
      
      const res = await axios.put(`${getInventoryEndpoint(type)}/${itemId}`, payload, { headers: getAuthHeaders() });
      setItems((prev) => prev.map((i) => (i.id === itemId ? res.data : i)));
      toast.success(`${item.name} ${res.data.available ? "enabled" : "disabled"}`);
      fetchData(); // Refresh summary
    } catch { toast.error("Failed to toggle item"); }
  };

  const handleSaveInventoryItem = async () => {
    const { type, mode, item } = inventoryDialog;
    if (!inventoryForm.name.trim()) { toast.error("Name is required"); return; }
    
    // Client-side duplicate check
    const [currentItems] = getInventoryState(type);
    const normalizedNew = normalizeForDuplicateCheck(inventoryForm.name);
    const duplicate = currentItems.find(existing => {
      if (mode === 'edit' && existing.id === item?.id) return false;
      return normalizeForDuplicateCheck(existing.name) === normalizedNew;
    });
    
    if (duplicate) {
      toast.error(`"${inventoryForm.name}" already exists as "${duplicate.name}". Duplicates are not allowed.`);
      return;
    }
    
    try {
      const payload = { 
        name: inventoryForm.name.trim(), 
        available: inventoryForm.available, 
        sort_order: parseInt(inventoryForm.sort_order) || 0 
      };
      
      if (type === "pizza-topping") payload.category = inventoryForm.category;
      if (type === "dipping-sauce") payload.price = parseFloat(inventoryForm.price) || 0.79;
      if (type === "extra-side") {
        if (!inventoryForm.price) { toast.error("Price is required"); return; }
        payload.price = parseFloat(inventoryForm.price);
        payload.description = inventoryForm.description || "";
      }
      
      const [items, setItems] = getInventoryState(type);
      
      if (mode === "create") {
        const res = await axios.post(getInventoryEndpoint(type), payload, { headers: getAuthHeaders() });
        setItems((prev) => [...prev, res.data]);
        toast.success(`${inventoryForm.name} created`);
      } else {
        const res = await axios.put(`${getInventoryEndpoint(type)}/${item.id}`, payload, { headers: getAuthHeaders() });
        setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
        toast.success(`${inventoryForm.name} updated`);
      }
      
      setInventoryDialog({ open: false, type: null, mode: "create", item: null });
      fetchData(); // Refresh summary
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save item");
    }
  };

  const handleDeleteInventoryItem = async (type, itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${getInventoryEndpoint(type)}/${itemId}`, { headers: getAuthHeaders() });
      const [, setItems] = getInventoryState(type);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      toast.success(`${itemName} deleted`);
      fetchData(); // Refresh summary
    } catch { toast.error("Failed to delete item"); }
  };

  // ==================== Concept Hours Handlers ====================
  const handleQuickClose = async (conceptKey, conceptName) => {
    if (!window.confirm(`Close ${conceptName}? Customers won't be able to order.`)) return;
    try {
      await axios.post(`${API}/admin/concepts/${conceptKey}/quick-close`, {}, { headers: getAuthHeaders() });
      toast.success(`${conceptName} is now closed`);
      fetchData();
    } catch { toast.error("Failed to close concept"); }
  };

  const handleQuickOpen = async (conceptKey, conceptName) => {
    try {
      await axios.post(`${API}/admin/concepts/${conceptKey}/quick-open`, {}, { headers: getAuthHeaders() });
      toast.success(`${conceptName} is now open`);
      fetchData();
    } catch { toast.error("Failed to open concept"); }
  };

  const handleClearOverride = async (conceptKey, conceptName) => {
    try {
      await axios.post(`${API}/admin/concepts/${conceptKey}/clear-override`, {}, { headers: getAuthHeaders() });
      toast.success(`${conceptName} now follows its schedule`);
      fetchData();
    } catch { toast.error("Failed to clear override"); }
  };

  const handleSaveRestaurantSettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(`${API}/admin/restaurant-settings`, restaurantSettings, { headers: getAuthHeaders() });
      toast.success("Settings saved successfully");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleTogglePayments = async (type, enabled) => {
    // Validation: at least one payment method must be enabled
    if (type === "online" && !enabled && !storePaymentEnabled) {
      toast.error("At least one payment method must be enabled");
      return;
    }
    if (type === "store" && !enabled && !paymentsEnabled) {
      toast.error("At least one payment method must be enabled");
      return;
    }
    
    setPaymentsSaving(true);
    try {
      const payload = type === "online" 
        ? { payments_enabled: enabled, store_payment_enabled: storePaymentEnabled }
        : { payments_enabled: paymentsEnabled, store_payment_enabled: enabled };
      
      await axios.put(`${API}/admin/payment-settings`, payload, { headers: getAuthHeaders() });
      
      if (type === "online") {
        setPaymentsEnabled(enabled);
        toast.success(`Online payments ${enabled ? "enabled" : "disabled"}`);
      } else {
        setStorePaymentEnabled(enabled);
        toast.success(`Pay at Store ${enabled ? "enabled" : "disabled"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update payment settings");
    } finally {
      setPaymentsSaving(false);
    }
  };

  // ==================== Employee CRUD Handlers ====================
  const openEmployeeDialog = (mode = "create", employee = null) => {
    setEmployeeDialog({ open: true, mode, employee });
    if (mode === "edit" && employee) {
      setEmployeeForm({
        name: employee.name || "",
        pin: employee.pin || "",
        role: employee.role || "Cashier",
      });
    } else {
      setEmployeeForm({ name: "", pin: "", role: "Cashier" });
    }
  };

  const handleSaveEmployee = async () => {
    if (!employeeForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!employeeForm.pin || employeeForm.pin.length !== 4 || !/^\d{4}$/.test(employeeForm.pin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    
    setInstoreSaving(true);
    try {
      if (employeeDialog.mode === "create") {
        const res = await axios.post(`${API}/admin/employees`, {
          name: employeeForm.name.trim(),
          pin: employeeForm.pin,
          role: employeeForm.role.trim() || "Cashier",
        }, { headers: getAuthHeaders() });
        setEmployees(prev => [...prev, res.data]);
        toast.success(`Employee "${employeeForm.name}" added`);
      } else {
        const res = await axios.put(`${API}/admin/employees/${employeeDialog.employee.id}`, {
          name: employeeForm.name.trim(),
          pin: employeeForm.pin,
          role: employeeForm.role.trim() || "Cashier",
        }, { headers: getAuthHeaders() });
        setEmployees(prev => prev.map(e => e.id === employeeDialog.employee.id ? res.data : e));
        toast.success(`Employee "${employeeForm.name}" updated`);
      }
      setEmployeeDialog({ open: false, mode: "create", employee: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save employee");
    } finally {
      setInstoreSaving(false);
    }
  };

  const handleDeleteEmployee = async (employee) => {
    if (!window.confirm(`Delete employee "${employee.name}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/admin/employees/${employee.id}`, { headers: getAuthHeaders() });
      setEmployees(prev => prev.filter(e => e.id !== employee.id));
      toast.success(`Employee "${employee.name}" deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete employee");
    }
  };

  const handleToggleInstoreMode = async (enabled) => {
    setInstoreSaving(true);
    try {
      await axios.put(`${API}/admin/instore-toggle`, { enabled }, { headers: getAuthHeaders() });
      setInstoreEnabled(enabled);
      toast.success(`In-Store Mode ${enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update in-store mode");
    } finally {
      setInstoreSaving(false);
    }
  };

  const handleTogglePromoCode = async (code, enabled) => {
    setPromoLoading(prev => ({ ...prev, [code]: true }));
    try {
      await axios.put(`${API}/admin/promo-codes/${code}`, { enabled }, { headers: getAuthHeaders() });
      setPromoCodes(prev => prev.map(p => p.code === code ? { ...p, enabled } : p));
      toast.success(`${code} ${enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update promo code");
    } finally {
      setPromoLoading(prev => ({ ...prev, [code]: false }));
    }
  };

  const handleToggleWelcomePopup = async (enabled) => {
    try {
      await axios.put(`${API}/admin/welcome-popup-settings`, { welcome_popup_enabled: enabled }, { headers: getAuthHeaders() });
      setWelcomePopupEnabled(enabled);
      toast.success(`Welcome popup ${enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update setting");
    }
  };

  const handlePriceEdit = (itemId, newPrice) => { setEditingPrices((prev) => ({ ...prev, [itemId]: newPrice })); };

  const handleSavePrice = async (itemId) => {
    const newPrice = parseFloat(editingPrices[itemId]);
    if (isNaN(newPrice) || newPrice < 0) { toast.error("Invalid price"); return; }
    try {
      const res = await axios.put(`${API}/admin/menu/${itemId}`, { price: newPrice }, { headers: getAuthHeaders() });
      setMenuItems((prev) => prev.map((m) => (m.id === itemId ? res.data : m)));
      setEditingPrices((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      toast.success(`Price updated to $${newPrice.toFixed(2)}`);
    } catch { toast.error("Failed to update price"); }
  };

  const handleToggleMenuItem = async (itemId, currentAvailable) => {
    try {
      const res = await axios.put(`${API}/admin/menu/${itemId}`, { available: !currentAvailable }, { headers: getAuthHeaders() });
      setMenuItems((prev) => prev.map((m) => (m.id === itemId ? res.data : m)));
      toast.success(`Item ${res.data.available ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to toggle item"); }
  };

  const handleSavePrinterIp = async () => {
    try {
      await axios.put(`${API}/admin/settings`, { printer_ip: printerIp }, { headers: getAuthHeaders() });
      toast.success("Printer IP saved");
    } catch { toast.error("Failed to save settings"); }
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setDateRange({ from: start, to: end });
  };

  // CMS Functions
  const openCreateItem = () => {
    setItemForm({
      concept: "chicken", category: "", name: "", size: "", price: "",
      description: "", image: "", requires_piece_selection: false, available: true,
    });
    setItemDialog({ open: true, mode: "create", item: null });
  };

  const openEditItem = (item) => {
    setItemForm({
      concept: item.concept || "chicken",
      category: item.category || "",
      name: item.name || "",
      size: item.size || "",
      price: item.price?.toString() || "",
      description: item.description || "",
      image: item.image || "",
      requires_piece_selection: item.requires_piece_selection || false,
      available: item.available !== false,
    });
    setItemDialog({ open: true, mode: "edit", item });
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim() || !itemForm.category || !itemForm.price) {
      toast.error("Name, category, and price are required");
      return;
    }
    const price = parseFloat(itemForm.price);
    if (isNaN(price) || price < 0) {
      toast.error("Invalid price");
      return;
    }
    try {
      const payload = {
        concept: itemForm.concept,
        category: itemForm.category,
        name: itemForm.name.trim(),
        size: itemForm.size.trim(),
        price: price,
        description: itemForm.description.trim(),
        image: itemForm.image,
        requires_piece_selection: itemForm.requires_piece_selection,
        available: itemForm.available,
        published: false, // New/edited items are unpublished drafts
      };

      if (itemDialog.mode === "create") {
        const res = await axios.post(`${API}/admin/menu`, payload, { headers: getAuthHeaders() });
        setMenuItems((prev) => [...prev, res.data]);
        toast.success("Menu item created (unpublished draft)");
      } else {
        const res = await axios.put(`${API}/admin/menu/${itemDialog.item.id}`, payload, { headers: getAuthHeaders() });
        setMenuItems((prev) => prev.map((m) => (m.id === itemDialog.item.id ? res.data : m)));
        toast.success("Menu item updated (changes unpublished)");
      }
      setHasUnpublished(true);
      setItemDialog({ open: false, mode: "create", item: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save menu item");
    }
  };

  const handleDeleteItem = async () => {
    try {
      await axios.delete(`${API}/admin/menu/${deleteDialog.id}`, { headers: getAuthHeaders() });
      setMenuItems((prev) => prev.filter((m) => m.id !== deleteDialog.id));
      toast.success("Menu item deleted");
      setDeleteDialog({ open: false, type: null, id: null, name: "" });
    } catch { toast.error("Failed to delete menu item"); }
  };

  const openCreateCategory = () => {
    setCategoryForm({ concept: "chicken", name: "", key: "", published: false, sort_order: 0 });
    setCategoryDialog({ open: true, mode: "create", category: null });
  };

  const openEditCategory = (cat) => {
    setCategoryForm({
      concept: cat.concept || "chicken",
      name: cat.name || "",
      key: cat.key || "",
      published: cat.published !== false,
      sort_order: cat.sort_order || 0,
    });
    setCategoryDialog({ open: true, mode: "edit", category: cat });
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim() || !categoryForm.key.trim()) {
      toast.error("Name and key are required");
      return;
    }
    try {
      const payload = {
        concept: categoryForm.concept,
        name: categoryForm.name.trim(),
        key: categoryForm.key.trim().toLowerCase().replace(/\s+/g, "_"),
        published: categoryForm.published,
        sort_order: parseInt(categoryForm.sort_order) || 0,
      };

      if (categoryDialog.mode === "create") {
        const res = await axios.post(`${API}/admin/categories`, payload, { headers: getAuthHeaders() });
        setCategories((prev) => [...prev, res.data]);
        toast.success("Category created");
      } else {
        const res = await axios.put(`${API}/admin/categories/${categoryDialog.category.id}`, payload, { headers: getAuthHeaders() });
        setCategories((prev) => prev.map((c) => (c.id === categoryDialog.category.id ? res.data : c)));
        toast.success("Category updated");
      }
      setCategoryDialog({ open: false, mode: "create", category: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save category");
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await axios.delete(`${API}/admin/categories/${deleteDialog.id}`, { headers: getAuthHeaders() });
      setCategories((prev) => prev.filter((c) => c.id !== deleteDialog.id));
      toast.success("Category deleted");
      setDeleteDialog({ open: false, type: null, id: null, name: "" });
    } catch { toast.error("Failed to delete category"); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      // Publish all unpublished items
      const unpublishedItems = menuItems.filter(i => i.published === false);
      for (const item of unpublishedItems) {
        await axios.put(`${API}/admin/menu/${item.id}`, { published: true }, { headers: getAuthHeaders() });
      }
      // Refresh data
      await fetchData();
      setHasUnpublished(false);
      toast.success(`Published ${unpublishedItems.length} menu item(s) to live menu`);
    } catch (e) {
      toast.error("Failed to publish changes");
    } finally {
      setPublishing(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be less than 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setItemForm((prev) => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleLogout = () => { logout(); navigate("/admin/login"); };
  const statusColor = (s) => ({ pending: "bg-amber-100 text-amber-700", accepted: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700" }[s] || "bg-slate-100 text-slate-700");

  const conceptGroups = {
    chicken: menuItems.filter((i) => i.concept === "chicken"),
    pizza: menuItems.filter((i) => i.concept === "pizza"),
    lunch: menuItems.filter((i) => i.concept === "lunch"),
  };

  // Concept colors for reuse
  const CONCEPT_COLORS = { chicken: "#F97316", pizza: "#10B981", lunch: "#2E7D32" };
  const CONCEPT_LABELS = { chicken: "Krispy Krunchy Chicken", pizza: "Hunt Brothers Pizza", lunch: "Island Grill & Deli" };

  // Bulk toggle handlers for Quick Edit
  const handleBulkToggle = async (concept, enableAll) => {
    const items = conceptGroups[concept];
    const toToggle = items.filter(item => enableAll ? !item.available : item.available);
    if (toToggle.length === 0) {
      toast.info(`All ${CONCEPT_LABELS[concept]} items already ${enableAll ? "enabled" : "disabled"}`);
      return;
    }
    
    let successCount = 0;
    for (const item of toToggle) {
      try {
        await axios.put(`${API}/admin/menu/${item.id}`, { available: enableAll }, { headers: getAuthHeaders() });
        successCount++;
      } catch (e) {
        console.error(`Failed to toggle ${item.name}:`, e);
      }
    }
    
    if (successCount > 0) {
      setMenuItems(prev => prev.map(item => 
        item.concept === concept && toToggle.some(t => t.id === item.id) 
          ? { ...item, available: enableAll } 
          : item
      ));
      toast.success(`${enableAll ? "Enabled" : "Disabled"} ${successCount} ${CONCEPT_LABELS[concept]} item${successCount !== 1 ? "s" : ""}`);
    }
  };

  // Concept schedule state
  const [conceptSchedules, setConceptSchedules] = useState({});
  const [scheduleSaving, setScheduleSaving] = useState({});
  const [expandedConcepts, setExpandedConcepts] = useState({});

  // Initialize concept schedules from fetched data
  useEffect(() => {
    const schedules = {};
    concepts.forEach(c => {
      const key = c.conceptKey || c.key;
      schedules[key] = c.schedule || {
        monday: { enabled: true, open: "05:00", close: "21:00" },
        tuesday: { enabled: true, open: "05:00", close: "21:00" },
        wednesday: { enabled: true, open: "05:00", close: "21:00" },
        thursday: { enabled: true, open: "05:00", close: "21:00" },
        friday: { enabled: true, open: "05:00", close: "22:00" },
        saturday: { enabled: true, open: "05:00", close: "22:00" },
        sunday: { enabled: true, open: "05:00", close: "20:00" },
      };
    });
    setConceptSchedules(schedules);
  }, [concepts]);

  const handleScheduleChange = (conceptKey, day, field, value) => {
    setConceptSchedules(prev => ({
      ...prev,
      [conceptKey]: {
        ...prev[conceptKey],
        [day]: {
          ...prev[conceptKey]?.[day],
          [field]: value
        }
      }
    }));
  };

  const handleSaveSchedule = async (conceptKey) => {
    setScheduleSaving(prev => ({ ...prev, [conceptKey]: true }));
    try {
      await axios.put(`${API}/admin/concepts/${conceptKey}/schedule`, 
        conceptSchedules[conceptKey], 
        { headers: getAuthHeaders() }
      );
      toast.success(`${CONCEPT_LABELS[conceptKey]} schedule saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    } finally {
      setScheduleSaving(prev => ({ ...prev, [conceptKey]: false }));
    }
  };

  const getCategoriesForConcept = (concept) => categories.filter(c => c.concept === concept);

  // Bulk Price Update Handler
  const handleBulkPriceUpdate = async () => {
    const val = parseFloat(bulkPriceForm.value);
    if (isNaN(val) || val <= 0) {
      toast.error("Please enter a valid positive number");
      return;
    }
    
    setBulkPriceSaving(true);
    try {
      const res = await axios.post(`${API}/admin/menu/bulk-update`, {
        concept: bulkPriceForm.concept,
        action: bulkPriceForm.action,
        value: val
      }, { headers: getAuthHeaders() });
      
      toast.success(`Updated ${res.data.updated} items in ${CONCEPT_LABELS[bulkPriceForm.concept]}`);
      setBulkPriceDialog(false);
      setBulkPriceForm({ concept: "chicken", action: "increase_pct", value: "" });
      fetchData(); // Refresh menu items
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update prices");
    } finally {
      setBulkPriceSaving(false);
    }
  };

  // Kitchen Availability Handlers
  const handleToggleBiscuit = async () => {
    setKitchenSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings/biscuit-availability`, 
        { available: !biscuitAvailable }, 
        { headers: getAuthHeaders() }
      );
      setBiscuitAvailable(res.data.available);
      toast.success(`Biscuits ${res.data.available ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update biscuit availability");
    } finally {
      setKitchenSaving(false);
    }
  };

  const handleToggleCrust = async (crustType, currentValue) => {
    // Validate at least one crust remains available
    if (crustType === "regular" && currentValue && !crustThinAvailable) {
      toast.error("At least one crust type must be available");
      return;
    }
    if (crustType === "thin" && currentValue && !crustRegularAvailable) {
      toast.error("At least one crust type must be available");
      return;
    }

    setKitchenSaving(true);
    try {
      const payload = {
        regular: crustType === "regular" ? !currentValue : crustRegularAvailable,
        thin: crustType === "thin" ? !currentValue : crustThinAvailable
      };
      const res = await axios.put(`${API}/admin/settings/crust-availability`, 
        payload, 
        { headers: getAuthHeaders() }
      );
      setCrustRegularAvailable(res.data.regular);
      setCrustThinAvailable(res.data.thin);
      toast.success(`${crustType === "regular" ? "Regular" : "Thin"} crust ${res.data[crustType] ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update crust availability");
    } finally {
      setKitchenSaving(false);
    }
  };

  const handleTogglePizzaLoyalty = async () => {
    setKitchenSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/settings/pizza-loyalty`,
        { enabled: !pizzaLoyaltyEnabled },
        { headers: getAuthHeaders() }
      );
      setPizzaLoyaltyEnabled(res.data.enabled);
      toast.success(`Pizza loyalty ${res.data.enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update pizza loyalty");
    } finally {
      setKitchenSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.sandWhite }}><p className="text-slate-500">Loading...</p></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.sandWhite }} data-testid="admin-dashboard">
      <header className="h-14 flex items-center justify-between px-6" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full object-cover" />
          <h1 className="text-lg font-extrabold text-white" style={{ fontFamily: "Montserrat" }}>Admin Portal</h1>
          {hasUnpublished && (
            <Badge className="text-[10px]" style={{ backgroundColor: BRAND_COLORS.sunsetOrange, color: "white" }}>
              Unpublished Changes
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasUnpublished && (
            <Button
              data-testid="publish-btn"
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white font-semibold"
              onClick={handlePublish}
              disabled={publishing}
            >
              {publishing ? "Publishing..." : "Publish Changes"}
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            className="border-white/30 text-white hover:bg-white/10"
            onClick={() => window.open("/admin/menu-editor", "_blank")}
            data-testid="menu-editor-link"
          >
            <ExternalLink className="h-4 w-4 mr-1" /> Menu Editor
          </Button>
          <Button data-testid="admin-logout-btn" variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <div className="bg-white border-b border-slate-100 px-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-11 bg-transparent gap-2 flex-wrap" data-testid="admin-tabs">
            {[
              { v: "dashboard", icon: BarChart3, label: "Dashboard" },
              { v: "inventory", icon: Package, label: "Inventory" },
              { v: "cms", icon: Layers, label: "CMS" },
              { v: "menu", icon: DollarSign, label: "Quick Edit" },
              { v: "orders", icon: Package, label: "Orders" },
              { v: "messages", icon: MessageSquare, label: "Messages" },
              { v: "promos", icon: Award, label: "Promos" },
              { v: "settings", icon: Settings, label: "Settings" },
            ].map(({ v, icon: Icon, label }) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:border-b-2 rounded-none text-xs sm:text-sm" style={{ borderColor: tab === v ? BRAND_COLORS.sunsetOrange : "transparent" }}>
                <Icon className="h-4 w-4 mr-1" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {/* Dashboard */}
        {tab === "dashboard" && (
          <div className="space-y-6 animate-fade-in">
            {/* Concept Status Widget */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Clock className="h-4 w-4" style={{ color: BRAND_COLORS.deepTeal }} />
                  Concept Status (Real-time)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {concepts.map((concept) => {
                    const status = concept.currentStatus || {};
                    const isOpen = status.isOpen;
                    const closesIn = status.closesIn;
                    const isForced = status.status === "force_open" || status.status === "force_closed";
                    
                    return (
                      <div 
                        key={concept.conceptKey}
                        className={`p-4 rounded-xl border-2 ${isOpen ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${isOpen ? "bg-green-500" : "bg-red-500"}`} />
                            <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>
                              {concept.conceptName}
                            </span>
                          </div>
                          {isForced && (
                            <Badge className="text-[10px] bg-amber-100 text-amber-700">Override</Badge>
                          )}
                        </div>
                        
                        <p className={`text-xs mb-3 ${isOpen ? "text-green-700" : "text-red-700"}`}>
                          {isOpen ? (
                            closesIn ? `Closes in ${formatMinutes(closesIn)}` : status.message
                          ) : (
                            status.nextOpen ? `Opens ${status.nextOpen}` : status.message
                          )}
                        </p>
                        
                        {isOpen ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full h-8 text-xs border-red-300 text-red-600 hover:bg-red-100"
                            onClick={() => handleQuickClose(concept.conceptKey, concept.conceptName)}
                            data-testid={`quick-close-${concept.conceptKey}`}
                          >
                            <PowerOff className="h-3 w-3 mr-1" /> Quick Close
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            className="w-full h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleQuickOpen(concept.conceptKey, concept.conceptName)}
                            data-testid={`quick-open-${concept.conceptKey}`}
                          >
                            <Power className="h-3 w-3 mr-1" /> Quick Open
                          </Button>
                        )}
                        {isForced && (
                          <button
                            className="w-full text-[11px] mt-2 underline underline-offset-2 text-slate-500 hover:text-slate-700"
                            onClick={() => handleClearOverride(concept.conceptKey, concept.conceptName)}
                            data-testid={`clear-override-${concept.conceptKey}`}
                          >
                            Resume schedule
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setQuickRange(0)} data-testid="range-today">Today</Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setQuickRange(7)} data-testid="range-7d">Last 7 Days</Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setQuickRange(30)} data-testid="range-30d">Last 30 Days</Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setDateRange({ from: null, to: null })}>All Time</Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs" data-testid="custom-date-btn">
                    <CalendarDays className="h-3 w-3 mr-1" />
                    {dateRange.from ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to || new Date())}` : "Custom Range"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="range" selected={dateRange} onSelect={(r) => setDateRange(r || { from: null, to: null })} numberOfMonths={1} />
                </PopoverContent>
              </Popover>
            </div>

            {analytics && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard icon={Package} label="Orders" value={analytics.range_orders} color="#FF6347" />
                  <StatCard icon={TrendingUp} label="Revenue" value={`$${analytics.range_revenue.toFixed(2)}`} color="#10B981" />
                  <StatCard icon={DollarSign} label="Avg Ticket" value={`$${analytics.avg_ticket.toFixed(2)}`} color="#8B5CF6" />
                  <StatCard icon={CheckCircle} label="Completed" value={analytics.by_status.completed} color="#22C55E" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="bg-white border-slate-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                        <Award className="h-4 w-4 text-amber-500" /> Top 5 Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.top_items.length === 0 ? (
                        <p className="text-sm text-slate-400">No data yet</p>
                      ) : (
                        <div className="space-y-2">
                          {analytics.top_items.map((item, i) => (
                            <div key={item.name} className="flex items-center justify-between py-1.5">
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                                <span className="text-sm text-slate-700 font-medium truncate max-w-[200px]">{item.name}</span>
                              </div>
                              <Badge variant="outline" className="font-mono text-xs">{item.count} sold</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-slate-100">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">Orders by Concept</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(analytics.by_concept).map(([concept, count]) => {
                        const colors = { chicken: "#F97316", pizza: "#10B981", burgers: "#F59E0B" };
                        const maxCount = Math.max(...Object.values(analytics.by_concept), 1);
                        return (
                          <div key={concept}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium text-slate-700 capitalize">{concept}</span>
                              <span className="font-mono text-slate-500">{count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: colors[concept] }} />
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}

        {/* CMS - Full Menu Management */}
        {tab === "cms" && (
          <div className="space-y-6 animate-fade-in">
            {/* Categories Management */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold" style={{ fontFamily: "Manrope" }}>Categories</CardTitle>
                <Button data-testid="add-category-btn" size="sm" className="h-8" style={{ backgroundColor: "#FF6347" }} onClick={openCreateCategory}>
                  <Plus className="h-4 w-4 mr-1" /> Add Category
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs">Key</TableHead>
                      <TableHead className="text-xs">Concept</TableHead>
                      <TableHead className="text-xs">Order</TableHead>
                      <TableHead className="text-xs">Published</TableHead>
                      <TableHead className="text-xs w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat) => (
                      <TableRow key={cat.id} data-testid={`category-row-${cat.id}`}>
                        <TableCell className="text-sm font-medium">{cat.name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px] font-mono">{cat.key}</Badge></TableCell>
                        <TableCell>
                          <Badge style={{ backgroundColor: CONCEPT_OPTIONS.find(c => c.value === cat.concept)?.color + "20", color: CONCEPT_OPTIONS.find(c => c.value === cat.concept)?.color }} className="text-[10px]">
                            {cat.concept}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{cat.sort_order}</TableCell>
                        <TableCell>
                          {cat.published ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]"><Eye className="h-3 w-3 mr-1" />Live</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 text-[10px]"><EyeOff className="h-3 w-3 mr-1" />Draft</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button data-testid={`edit-category-${cat.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditCategory(cat)}>
                              <Pencil className="h-3.5 w-3.5 text-slate-500" />
                            </Button>
                            <Button data-testid={`delete-category-${cat.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setDeleteDialog({ open: true, type: "category", id: cat.id, name: cat.name })}>
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Menu Items Management */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold" style={{ fontFamily: "Manrope" }}>Menu Items</CardTitle>
                <Button data-testid="add-item-btn" size="sm" className="h-8" style={{ backgroundColor: "#FF6347" }} onClick={openCreateItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Image</TableHead>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs">Price</TableHead>
                        <TableHead className="text-xs">Piece Sel.</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs w-32">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {menuItems.map((item) => (
                        <TableRow key={item.id} data-testid={`cms-item-row-${item.id}`} className={item.published === false ? "bg-amber-50/50" : (!item.available ? "bg-red-50/30" : "")}>
                          <TableCell>
                            {item.image ? (
                              <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                <ImageIcon className="h-4 w-4 text-slate-400" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{item.name}</p>
                              {item.size && <p className="text-xs text-slate-500">{item.size}</p>}
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{item.category?.replace(/_/g, " ")}</Badge></TableCell>
                          <TableCell className="font-mono text-sm">${item.price?.toFixed(2)}</TableCell>
                          <TableCell>
                            {item.requires_piece_selection ? (
                              <Badge className="bg-orange-100 text-orange-700 text-[10px]">Yes</Badge>
                            ) : (
                              <span className="text-xs text-slate-400">No</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {item.published === false ? (
                                <Badge className="bg-amber-100 text-amber-700 text-[10px]">Draft</Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">Published</Badge>
                              )}
                              {item.available ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">Active</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-[10px]">Disabled</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Switch 
                                data-testid={`cms-toggle-${item.id}`}
                                checked={item.available}
                                onCheckedChange={() => handleToggleMenuItem(item.id, item.available)}
                                className="scale-75"
                              />
                              <Button data-testid={`edit-item-${item.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditItem(item)}>
                                <Pencil className="h-3.5 w-3.5 text-slate-500" />
                              </Button>
                              <Button data-testid={`delete-item-${item.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setDeleteDialog({ open: true, type: "item", id: item.id, name: item.name })}>
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <p className="text-sm text-amber-800">
                  <strong>How Publishing Works:</strong> When you create or edit menu items, they are saved as drafts. 
                  Click the <strong>&quot;Publish Changes&quot;</strong> button in the header to make all draft changes live on the customer menu.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Edit (Price) */}
        {tab === "menu" && (
          <div className="space-y-6 animate-fade-in">
            {/* Bulk Price Update Button */}
            <div className="flex justify-end">
              <Button
                data-testid="bulk-price-update-btn"
                variant="outline"
                className="h-9"
                style={{ borderColor: BRAND_COLORS.sunsetOrange, color: BRAND_COLORS.sunsetOrange }}
                onClick={() => setBulkPriceDialog(true)}
              >
                <DollarSign className="h-4 w-4 mr-1" /> Bulk Price Update
              </Button>
            </div>

            {Object.entries(conceptGroups).map(([ck, items]) => {
              if (items.length === 0) return null;
              const label = CONCEPT_LABELS[ck];
              const color = CONCEPT_COLORS[ck];
              const availableCount = items.filter(i => i.available).length;
              return (
                <Card key={ck} className="bg-white border-slate-100">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />{label}
                        <Badge variant="outline" className="text-[10px] ml-2" style={{ borderColor: color, color: color }}>
                          {availableCount} / {items.length} active
                        </Badge>
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button 
                          data-testid={`enable-all-${ck}`}
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          style={{ borderColor: "#10B981", color: "#10B981" }}
                          onClick={() => handleBulkToggle(ck, true)}
                        >
                          <Check className="h-3 w-3 mr-1" /> Enable All
                        </Button>
                        <Button 
                          data-testid={`disable-all-${ck}`}
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          style={{ borderColor: "#EF4444", color: "#EF4444" }}
                          onClick={() => handleBulkToggle(ck, false)}
                        >
                          <X className="h-3 w-3 mr-1" /> Disable All
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Item</TableHead>
                          <TableHead className="text-xs">Category</TableHead>
                          <TableHead className="text-xs">Size</TableHead>
                          <TableHead className="text-xs">Price</TableHead>
                          <TableHead className="text-xs">Active</TableHead>
                          <TableHead className="text-xs w-20"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => {
                          const isEditing = editingPrices[item.id] !== undefined;
                          return (
                            <TableRow key={item.id} data-testid={`menu-item-row-${item.id}`} className={!item.available ? "bg-red-50/50" : ""}>
                              <TableCell className="text-sm font-medium">{item.name}</TableCell>
                              <TableCell><Badge variant="outline" className="text-[10px]">{item.category?.replace(/_/g, " ")}</Badge></TableCell>
                              <TableCell className="text-sm">{item.size || "-"}</TableCell>
                              <TableCell>
                                <Input data-testid={`price-input-${item.id}`} type="number" step="0.01"
                                  value={isEditing ? editingPrices[item.id] : item.price?.toFixed(2)}
                                  onChange={(e) => handlePriceEdit(item.id, e.target.value)}
                                  className="w-24 h-8 text-sm font-mono" />
                              </TableCell>
                              <TableCell>
                                <Switch data-testid={`menu-available-${item.id}`} checked={item.available}
                                  onCheckedChange={() => handleToggleMenuItem(item.id, item.available)} />
                              </TableCell>
                              <TableCell>
                                {isEditing && (
                                  <Button data-testid={`save-price-${item.id}`} size="sm" className="h-7 text-xs rounded-lg"
                                    style={{ backgroundColor: "#FF6347" }} onClick={() => handleSavePrice(item.id)}>
                                    <Save className="h-3 w-3 mr-1" />Save
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Inventory - Database Connected */}
        {tab === "inventory" && (
          <div className="animate-fade-in space-y-6">
            {/* Summary Banner */}
            {inventorySummary && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { label: "Pizza Toppings", data: inventorySummary.pizza_toppings, color: BRAND_COLORS.deepTeal },
                  { label: "Wing Sauces", data: inventorySummary.wing_sauces, color: BRAND_COLORS.sunsetOrange },
                  { label: "Dipping Sauces", data: inventorySummary.dipping_sauces, color: BRAND_COLORS.tropicalAqua },
                  { label: "Sides", data: inventorySummary.sides, color: "#8B5CF6" },
                  { label: "Extra Sides", data: inventorySummary.extra_sides, color: "#10B981" },
                  { label: "Lunch Sides", data: inventorySummary.lunch_sides, color: "#2E7D32" },
                ].map(({ label, data, color }) => (
                  <div key={label} className="p-3 rounded-xl bg-white border border-slate-100">
                    <p className="text-xs font-medium text-slate-500">{label}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-lg font-bold" style={{ color }}>{data?.available || 0}</span>
                      <span className="text-xs text-slate-400">available</span>
                      {data?.unavailable > 0 && (
                        <Badge className="bg-red-100 text-red-600 text-[10px] ml-auto">{data.unavailable} out</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              Toggle availability for items. Disabled items won&apos;t appear on the customer menu. All changes are saved to the database immediately.
            </p>

            {/* Kitchen Availability Card */}
            <Card className="bg-white border-slate-100 border-2 border-amber-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#F59E0B" }} />
                  Kitchen Availability
                  <Badge className="ml-2 text-[10px]" style={{ backgroundColor: "#FEF3C7", color: "#D97706" }}>
                    Quick Toggles
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-slate-500">
                  Quickly enable/disable items that may run out during service. Changes apply immediately to the customer menu.
                </p>
                
                {/* Biscuit Toggle */}
                <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${biscuitAvailable ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Biscuits Available</p>
                    <p className="text-xs text-slate-500">Turn off when biscuits are unavailable</p>
                  </div>
                  <Switch 
                    data-testid="biscuit-toggle"
                    checked={biscuitAvailable} 
                    onCheckedChange={handleToggleBiscuit}
                    disabled={kitchenSaving}
                  />
                </div>

                {/* Pizza Crust Toggles */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${crustRegularAvailable ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Regular Crust</p>
                      <p className="text-xs text-slate-500">Standard pizza crust</p>
                    </div>
                    <Switch 
                      data-testid="crust-regular-toggle"
                      checked={crustRegularAvailable} 
                      onCheckedChange={() => handleToggleCrust("regular", crustRegularAvailable)}
                      disabled={kitchenSaving}
                    />
                  </div>
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${crustThinAvailable ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Thin Crust</p>
                      <p className="text-xs text-slate-500">Thin & crispy crust</p>
                    </div>
                    <Switch 
                      data-testid="crust-thin-toggle"
                      checked={crustThinAvailable} 
                      onCheckedChange={() => handleToggleCrust("thin", crustThinAvailable)}
                      disabled={kitchenSaving}
                    />
                  </div>
                </div>

                {/* Pizza Loyalty Program Toggle - Phase 14 Batch C */}
                <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${pizzaLoyaltyEnabled ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"}`}>
                  <div>
                    <p className="font-semibold text-sm flex items-center gap-2" style={{ color: BRAND_COLORS.darkSlate }}>
                      Pizza Loyalty Program
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: "#FEF3C7", color: "#78350F" }}>
                        Buy 6, get 7th free
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      Tracks pizzas per customer phone. Resets on the 1st of each month.
                    </p>
                  </div>
                  <Switch
                    data-testid="pizza-loyalty-toggle"
                    checked={pizzaLoyaltyEnabled}
                    onCheckedChange={handleTogglePizzaLoyalty}
                    disabled={kitchenSaving}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Pizza Toppings */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_COLORS.deepTeal }} />
                  Pizza Toppings
                  <Badge variant="outline" className="text-[10px] ml-2">{pizzaToppings.length} items</Badge>
                </CardTitle>
                <Button size="sm" className="h-8" style={{ backgroundColor: BRAND_COLORS.deepTeal }} onClick={() => openInventoryDialog("pizza-topping", "create")}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent>
                <div className="mb-3">
                  <p className="text-xs font-semibold text-slate-500 mb-2">MEATS</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {pizzaToppings.filter(t => t.category === "meats").map((topping) => (
                      <div key={topping.id} className={`flex items-center justify-between p-3 rounded-xl border ${topping.available ? "bg-white border-slate-100" : "bg-red-50 border-red-200"}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${topping.available ? "bg-green-500" : "bg-red-500"}`} />
                          <span className={`font-medium text-sm truncate ${!topping.available ? "text-red-600" : ""}`} style={{ color: topping.available ? BRAND_COLORS.darkSlate : undefined }}>{topping.name}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openInventoryDialog("pizza-topping", "edit", topping)}>
                            <Pencil className="h-3 w-3 text-slate-400" />
                          </Button>
                          <Switch checked={topping.available} onCheckedChange={() => handleToggleInventoryItem("pizza-topping", topping.id, topping.available)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2">VEGGIES</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {pizzaToppings.filter(t => t.category === "veggies").map((topping) => (
                      <div key={topping.id} className={`flex items-center justify-between p-3 rounded-xl border ${topping.available ? "bg-white border-slate-100" : "bg-red-50 border-red-200"}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${topping.available ? "bg-green-500" : "bg-red-500"}`} />
                          <span className={`font-medium text-sm truncate ${!topping.available ? "text-red-600" : ""}`} style={{ color: topping.available ? BRAND_COLORS.darkSlate : undefined }}>{topping.name}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openInventoryDialog("pizza-topping", "edit", topping)}>
                            <Pencil className="h-3 w-3 text-slate-400" />
                          </Button>
                          <Switch checked={topping.available} onCheckedChange={() => handleToggleInventoryItem("pizza-topping", topping.id, topping.available)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Wing Sauces */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_COLORS.sunsetOrange }} />
                  Wing Flavors
                  <Badge variant="outline" className="text-[10px] ml-2">{wingSauces.length} items</Badge>
                </CardTitle>
                <Button size="sm" className="h-8" style={{ backgroundColor: BRAND_COLORS.sunsetOrange }} onClick={() => openInventoryDialog("wing-sauce", "create")}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {wingSauces.map((sauce) => (
                    <div key={sauce.id} className={`flex items-center justify-between p-3 rounded-xl border ${sauce.available ? "bg-white border-slate-100" : "bg-red-50 border-red-200"}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sauce.available ? "bg-green-500" : "bg-red-500"}`} />
                        <span className={`font-medium text-sm truncate ${!sauce.available ? "text-red-600" : ""}`} style={{ color: sauce.available ? BRAND_COLORS.darkSlate : undefined }}>{sauce.name}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openInventoryDialog("wing-sauce", "edit", sauce)}>
                          <Pencil className="h-3 w-3 text-slate-400" />
                        </Button>
                        <Switch checked={sauce.available} onCheckedChange={() => handleToggleInventoryItem("wing-sauce", sauce.id, sauce.available)} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Dipping Sauces */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_COLORS.tropicalAqua }} />
                  Dipping Sauces
                  <Badge variant="outline" className="text-[10px] ml-2">{dippingSauces.length} items</Badge>
                </CardTitle>
                <Button size="sm" className="h-8" style={{ backgroundColor: BRAND_COLORS.tropicalAqua }} onClick={() => openInventoryDialog("dipping-sauce", "create")}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {dippingSauces.map((sauce) => (
                    <div key={sauce.id} className={`flex items-center justify-between p-3 rounded-xl border ${sauce.available ? "bg-white border-slate-100" : "bg-red-50 border-red-200"}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sauce.available ? "bg-green-500" : "bg-red-500"}`} />
                        <div className="min-w-0">
                          <span className={`font-medium text-sm block truncate ${!sauce.available ? "text-red-600" : ""}`} style={{ color: sauce.available ? BRAND_COLORS.darkSlate : undefined }}>{sauce.name}</span>
                          <span className="text-[10px] text-slate-400">${sauce.price?.toFixed(2) || "0.79"} extra</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openInventoryDialog("dipping-sauce", "edit", sauce)}>
                          <Pencil className="h-3 w-3 text-slate-400" />
                        </Button>
                        <Switch checked={sauce.available} onCheckedChange={() => handleToggleInventoryItem("dipping-sauce", sauce.id, sauce.available)} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Sides (already database-connected) */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#8B5CF6" }} />
                  Sides
                  <Badge variant="outline" className="text-[10px] ml-2">{sides.length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {sides.map((side) => (
                    <div key={side.id} data-testid={`side-toggle-${side.id}`} className={`flex items-center justify-between p-3 rounded-xl border ${side.available ? "bg-white border-slate-100" : "bg-red-50 border-red-200"}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${side.available ? "bg-green-500" : "bg-red-500"}`} />
                        <span className={`font-medium text-sm ${!side.available ? "text-red-600" : ""}`} style={{ color: side.available ? BRAND_COLORS.darkSlate : undefined }}>{side.name}</span>
                      </div>
                      <Switch data-testid={`side-switch-${side.id}`} checked={side.available} onCheckedChange={() => handleToggleSide(side.id)} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Extra Sides (NEW) */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#10B981" }} />
                  Extra Sides (Add-ons)
                  <Badge variant="outline" className="text-[10px] ml-2">{extraSides.length} items</Badge>
                </CardTitle>
                <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => openInventoryDialog("extra-side", "create")}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent>
                {extraSides.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No extra sides yet. Add items like &quot;Extra Fries&quot;, &quot;Add Bacon&quot;, etc.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {extraSides.map((side) => (
                      <div key={side.id} className={`flex items-center justify-between p-3 rounded-xl border ${side.available ? "bg-white border-slate-100" : "bg-red-50 border-red-200"}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${side.available ? "bg-green-500" : "bg-red-500"}`} />
                          <div className="min-w-0">
                            <span className={`font-medium text-sm block truncate ${!side.available ? "text-red-600" : ""}`} style={{ color: side.available ? BRAND_COLORS.darkSlate : undefined }}>{side.name}</span>
                            <span className="text-xs text-slate-500">${side.price?.toFixed(2)} • {side.description || "No description"}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openInventoryDialog("extra-side", "edit", side)}>
                            <Pencil className="h-3 w-3 text-slate-400" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleDeleteInventoryItem("extra-side", side.id, side.name)}>
                            <Trash2 className="h-3 w-3 text-red-400" />
                          </Button>
                          <Switch checked={side.available} onCheckedChange={() => handleToggleInventoryItem("extra-side", side.id, side.available)} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lunch Menu Sides (for Make it a Meal) */}
            <Card className="bg-white border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold" style={{ fontFamily: "Manrope", color: BRAND_COLORS.darkSlate }}>
                    Lunch Menu Sides
                    <Badge variant="outline" className="text-[10px] ml-2">{lunchSides.length} items</Badge>
                  </span>
                  <Button size="sm" className="h-8" style={{ backgroundColor: "#2E7D32" }} onClick={() => openInventoryDialog("lunch-side", "create")}>
                    <Plus className="h-3 w-3 mr-1" /> Add Side
                  </Button>
                </div>
                <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                  Available sides for &quot;Make it a Meal&quot; option in burger customization
                </p>
                {lunchSides.length === 0 ? (
                  <p className="text-xs text-center py-4" style={{ color: BRAND_COLORS.darkSlate + "66" }}>No lunch sides configured</p>
                ) : (
                  <div className="space-y-2">
                    {lunchSides.map((side) => (
                      <div key={side.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                        <div>
                          <span className={`text-sm font-medium ${!side.available ? "text-slate-400 line-through" : ""}`}>{side.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openInventoryDialog("lunch-side", "edit", side)}>
                            <Settings className="h-3 w-3 text-slate-400" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleDeleteInventoryItem("lunch-side", side.id, side.name)}>
                            <Trash2 className="h-3 w-3 text-red-400" />
                          </Button>
                          <Switch checked={side.available} onCheckedChange={() => handleToggleInventoryItem("lunch-side", side.id, side.available)} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="border-slate-200" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "10" }}>
              <CardContent className="p-4">
                <p className="text-sm" style={{ color: BRAND_COLORS.deepTeal }}>
                  <strong>Note:</strong> All changes are saved to the database immediately. 
                  Disabled items will be hidden from the customer menu in real-time.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Orders */}
        {tab === "orders" && (
          <div className="animate-fade-in">
            <Card className="bg-white border-slate-100">
              <CardHeader><CardTitle className="text-base font-bold" style={{ fontFamily: "Manrope" }}>Order History</CardTitle></CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Order ID</TableHead>
                        <TableHead className="text-xs">Customer</TableHead>
                        <TableHead className="text-xs">Phone</TableHead>
                        <TableHead className="text-xs">Items</TableHead>
                        <TableHead className="text-xs">Total</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-mono text-xs">{order.id.slice(0, 8)}</TableCell>
                          <TableCell className="text-sm font-medium">{order.customer_name}</TableCell>
                          <TableCell className="text-sm">{order.phone_number || "-"}</TableCell>
                          <TableCell className="text-sm">{order.items?.length || 0}</TableCell>
                          <TableCell className="font-mono text-sm font-semibold">${order.total?.toFixed(2)}</TableCell>
                          <TableCell><Badge className={`text-[10px] ${statusColor(order.status)}`}>{order.status}</Badge></TableCell>
                          <TableCell className="text-xs text-slate-500">{new Date(order.created_at).toLocaleTimeString()}</TableCell>
                        </TableRow>
                      ))}
                      {orders.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No orders yet</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Messages Tab (Phase 15) */}
        {tab === "messages" && (
          <div className="animate-fade-in space-y-4" data-testid="admin-messages-tab">
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <MessageSquare className="h-5 w-5" style={{ color: BRAND_COLORS.sunsetOrange }} />
                  Order Conversations
                </CardTitle>
                <p className="text-xs text-slate-500">All customer chats grouped by order — most recent first.</p>
              </CardHeader>
              <CardContent className="p-0">
                {messageOrders.length === 0 ? (
                  <div className="text-center py-10">
                    <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No customer messages yet.</p>
                  </div>
                ) : (
                  messageOrders.map((m) => (
                    <button
                      key={m.order_id}
                      data-testid={`admin-inbox-item-${m.order_id}`}
                      className="w-full flex items-start gap-3 px-4 py-3 border-b hover:bg-slate-50 text-left"
                      onClick={() => setChatOrderId(m.order_id)}
                    >
                      <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-bold text-white"
                           style={{ backgroundColor: BRAND_COLORS.tropicalAqua }}>
                        {(m.customer_name?.[0] || "#").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold truncate" style={{ color: BRAND_COLORS.darkSlate }}>
                            {m.customer_name || `Order ${m.order_id.slice(0, 6)}`}
                          </p>
                          {m.unread > 0 && (
                            <span className="bg-yellow-400 text-slate-900 text-[10px] font-bold rounded-full px-2 py-0.5">
                              {m.unread} unread
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{m.last_text || "…"}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Order #{m.order_id.slice(0, 8)}</p>
                      </div>
                    </button>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admin Chat Sheet */}
        <Sheet
          open={!!chatOrderId}
          onOpenChange={(o) => {
            if (!o) {
              setMessageOrders((prev) => prev.map((m) => m.order_id === chatOrderId ? { ...m, unread: 0 } : m));
              setChatOrderId(null);
            }
          }}
        >
          <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
            <SheetHeader className="px-4 py-3 border-b bg-white shrink-0">
              <SheetTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />
                Reply to Customer
              </SheetTitle>
              <p className="text-[11px] text-slate-500">Order #{chatOrderId ? chatOrderId.slice(0, 8) : ""}</p>
            </SheetHeader>
            <div className="flex-1 min-h-0">
              {chatOrderId && (
                <OrderChatPanel
                  orderId={chatOrderId}
                  viewer="staff"
                  senderName="Store"
                  authHeaders={getAuthHeaders()}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Promos Tab */}
        {tab === "promos" && (
          <div className="animate-fade-in space-y-6">
            {/* Welcome Popup Control */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <Bell className="h-5 w-5" style={{ color: "#F59E0B" }} />
                  First Order Discount Popup
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                  <div>
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Show Welcome Popup</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Display &quot;WELCOME10&quot; promo popup to first-time visitors
                    </p>
                  </div>
                  <Switch
                    data-testid="welcome-popup-toggle"
                    checked={welcomePopupEnabled}
                    onCheckedChange={handleToggleWelcomePopup}
                  />
                </div>
                <div className={`mt-3 p-3 rounded-xl border ${welcomePopupEnabled ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
                  <p className={`text-xs ${welcomePopupEnabled ? "text-green-700" : "text-amber-700"}`}>
                    <strong>Status:</strong> {welcomePopupEnabled 
                      ? "Popup is ENABLED - New customers will see WELCOME10 offer" 
                      : "Popup is DISABLED - No welcome popup will be shown"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Promo Codes Table */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <Award className="h-5 w-5" style={{ color: "#10B981" }} />
                  Promotional Codes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-semibold text-slate-600">Code</th>
                        <th className="text-left py-3 px-2 font-semibold text-slate-600">Discount</th>
                        <th className="text-left py-3 px-2 font-semibold text-slate-600">Applies To</th>
                        <th className="text-center py-3 px-2 font-semibold text-slate-600">Used</th>
                        <th className="text-center py-3 px-2 font-semibold text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoCodes.map((promo) => (
                        <tr key={promo.code} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="py-3 px-2">
                            <span className="font-mono font-bold" style={{ color: BRAND_COLORS.deepTeal }}>
                              {promo.code}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className="font-semibold" style={{ color: "#10B981" }}>
                              {promo.discount_value}% off
                            </span>
                          </td>
                          <td className="py-3 px-2 capitalize">
                            {promo.applies_to === "all" ? "Entire Order" : promo.applies_to}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Badge variant="secondary" className="text-xs">
                              {promo.times_used || 0}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Switch
                              data-testid={`promo-toggle-${promo.code}`}
                              checked={promo.enabled}
                              onCheckedChange={(v) => handleTogglePromoCode(promo.code, v)}
                              disabled={promoLoading[promo.code]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 text-xs text-slate-500">
                  <p>• <strong>ALL10/50:</strong> Applies to entire order</p>
                  <p>• <strong>PIZZA10/50:</strong> Only applies to pizza items</p>
                  <p>• <strong>KKC10/50:</strong> Only applies to Krispy Krunchy items</p>
                  <p>• <strong>LUNCH10:</strong> Only applies to Lunch Menu items</p>
                  <p>• <strong>SIDES50:</strong> Only applies to sides and snacks</p>
                  <p>• <strong>FREE100:</strong> 100% off for VIP/testing (use with caution)</p>
                  <p>• <strong>WELCOME10:</strong> First-time customer discount</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings */}
        {tab === "settings" && (
          <div className="animate-fade-in space-y-6">
            {/* Restaurant Settings */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <Store className="h-5 w-5" style={{ color: BRAND_COLORS.deepTeal }} />
                  General Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Restaurant Name</Label>
                    <Input
                      value={restaurantSettings.restaurantName || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, restaurantName: e.target.value }))}
                      className="mt-1"
                      placeholder="Dauphin Island Food Mart"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Phone Number</Label>
                    <Input
                      value={restaurantSettings.restaurantPhone || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, restaurantPhone: e.target.value }))}
                      className="mt-1"
                      placeholder="(251) 861-2320"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Address</Label>
                  <Input
                    value={restaurantSettings.restaurantAddress || ""}
                    onChange={(e) => setRestaurantSettings(p => ({ ...p, restaurantAddress: e.target.value }))}
                    className="mt-1"
                    placeholder="1000 Bienville Blvd, Dauphin Island, AL 36528"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Order Notification Email</Label>
                    <Input
                      value={restaurantSettings.orderNotificationEmail || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, orderNotificationEmail: e.target.value }))}
                      className="mt-1"
                      placeholder="admin@example.com"
                    />
                    <p className="text-xs text-slate-400 mt-1">Receive email when new orders come in</p>
                  </div>
                  <div>
                    <Label className="text-sm">Estimated Prep Time (minutes)</Label>
                    <Input
                      type="number"
                      value={restaurantSettings.estimatedPrepTime || 20}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, estimatedPrepTime: parseInt(e.target.value) || 20 }))}
                      className="mt-1"
                      placeholder="20"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Settings */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <Mail className="h-5 w-5" style={{ color: BRAND_COLORS.sunsetOrange }} />
                  Email Settings (Resend)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">
                  Configure Resend to send order confirmation and status update emails to customers.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Resend API Key</Label>
                    <Input
                      type="password"
                      value={restaurantSettings.resendApiKey || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, resendApiKey: e.target.value }))}
                      className="mt-1 font-mono"
                      placeholder="re_xxxxx..."
                    />
                    <p className="text-xs text-slate-400 mt-1">Get your key at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a></p>
                  </div>
                  <div>
                    <Label className="text-sm">From Email Address</Label>
                    <Input
                      value={restaurantSettings.resendFromEmail || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, resendFromEmail: e.target.value }))}
                      className="mt-1"
                      placeholder="orders@yourdomain.com"
                    />
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-500">
                    <strong>Status:</strong> {restaurantSettings.resendApiKey?.includes("***") ? "Configured (key hidden)" : restaurantSettings.resendApiKey ? "Configured" : "Not configured - Email logging to console"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* SMS Settings */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <MessageSquare className="h-5 w-5" style={{ color: BRAND_COLORS.tropicalAqua }} />
                  SMS Settings (Twilio)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">
                  Configure Twilio to send SMS notifications when orders are ready for pickup.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm">Account SID</Label>
                    <Input
                      value={restaurantSettings.twilioAccountSid || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, twilioAccountSid: e.target.value }))}
                      className="mt-1 font-mono"
                      placeholder="ACxxxx..."
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Auth Token</Label>
                    <Input
                      type="password"
                      value={restaurantSettings.twilioAuthToken || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, twilioAuthToken: e.target.value }))}
                      className="mt-1 font-mono"
                      placeholder="xxxxxx..."
                    />
                  </div>
                  <div>
                    <Label className="text-sm">From Phone Number</Label>
                    <Input
                      value={restaurantSettings.twilioFromPhone || ""}
                      onChange={(e) => setRestaurantSettings(p => ({ ...p, twilioFromPhone: e.target.value }))}
                      className="mt-1"
                      placeholder="+1234567890"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div>
                    <p className="text-sm font-medium" style={{ color: BRAND_COLORS.darkSlate }}>Enable SMS Notifications</p>
                    <p className="text-xs text-slate-400">Requires valid Twilio credentials</p>
                  </div>
                  <Switch
                    checked={restaurantSettings.smsEnabled || false}
                    onCheckedChange={(v) => setRestaurantSettings(p => ({ ...p, smsEnabled: v }))}
                    disabled={!restaurantSettings.twilioAccountSid || !restaurantSettings.twilioAuthToken}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Web Push Settings */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <Bell className="h-5 w-5" style={{ color: "#8B5CF6" }} />
                  Web Push Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">
                  Web push notifications are automatically enabled. VAPID keys are generated on server startup.
                </p>
                <div className="p-3 rounded-xl bg-green-50 border border-green-200">
                  <p className="text-xs text-green-700">
                    <strong>Status:</strong> Active - Customers can opt-in to receive order updates via browser notifications
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Kitchen Printer */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold" style={{ fontFamily: "Manrope" }}>Kitchen Printer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">Configure the ESC/POS thermal printer IP address. The printer connects via Ethernet on Port 9100.</p>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label className="text-sm text-slate-700">Printer IP Address</Label>
                    <Input
                      data-testid="printer-ip-input"
                      placeholder="e.g. 192.168.1.100"
                      value={printerIp}
                      onChange={(e) => setPrinterIp(e.target.value)}
                      className="mt-1 h-10 font-mono"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button data-testid="save-printer-ip-btn" className="h-10 rounded-xl font-semibold" style={{ backgroundColor: "#FF6347" }} onClick={handleSavePrinterIp}>
                      <Save className="h-4 w-4 mr-1" /> Save
                    </Button>
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-500">
                    <strong>Status:</strong> {printerIp ? `Configured (${printerIp}:9100) - Currently mocked to logs` : "Not configured - Printing to server logs"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Payment Settings - Stripe */}
            <Card className="bg-white border-slate-100">
              <CardHeader>
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <CreditCard className="h-5 w-5" style={{ color: "#635BFF" }} />
                  Payment Methods
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Pay Online Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                  <div>
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Enable &quot;Pay Online&quot; (Stripe)</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Allow customers to pay online at checkout using credit/debit cards
                    </p>
                    {paymentsEnabled && (
                      <p className="text-xs text-slate-400 mt-1">
                        Processing fee (3% + $0.30) applied to online orders
                      </p>
                    )}
                  </div>
                  <Switch
                    data-testid="payments-online-toggle"
                    checked={paymentsEnabled}
                    onCheckedChange={(v) => handleTogglePayments("online", v)}
                    disabled={paymentsSaving}
                  />
                </div>
                
                {/* Pay at Store Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                  <div>
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Enable &quot;Pay at Store&quot; (Cash/Card)</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Allow customers to pay at pickup with cash or card
                    </p>
                  </div>
                  <Switch
                    data-testid="payments-store-toggle"
                    checked={storePaymentEnabled}
                    onCheckedChange={(v) => handleTogglePayments("store", v)}
                    disabled={paymentsSaving}
                  />
                </div>
                
                <div className={`p-3 rounded-xl border ${paymentsEnabled || storePaymentEnabled ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                  <p className={`text-xs ${paymentsEnabled || storePaymentEnabled ? "text-green-700" : "text-red-700"}`}>
                    <strong>Status:</strong> {
                      paymentsEnabled && storePaymentEnabled 
                        ? "Both payment methods enabled" 
                        : paymentsEnabled 
                          ? "Online payments only" 
                          : storePaymentEnabled 
                            ? "Pay at Store only" 
                            : "⚠️ At least one payment method must be enabled"
                    }
                  </p>
                </div>
                <div className="text-xs text-slate-400">
                  <p>• Stripe Test Mode is currently active</p>
                  <p>• Test card: 4242 4242 4242 4242</p>
                </div>
              </CardContent>
            </Card>

            {/* In-Store Mode Settings */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <Store className="h-5 w-5" style={{ color: "#0066cc" }} />
                  In-Store Order Mode
                </CardTitle>
                <Button
                  data-testid="add-employee-btn"
                  size="sm"
                  className="h-8 text-white"
                  style={{ backgroundColor: "#0066cc" }}
                  onClick={() => openEmployeeDialog("create")}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Employee
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enable In-Store Mode */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                  <div>
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Enable In-Store Mode</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Allow employees to take orders at the counter
                    </p>
                  </div>
                  <Switch
                    data-testid="instore-mode-toggle"
                    checked={instoreEnabled}
                    onCheckedChange={handleToggleInstoreMode}
                    disabled={instoreSaving}
                  />
                </div>
                
                {/* Employee List */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Employees</Label>
                  {employees.length === 0 ? (
                    <div className="p-4 rounded-xl border border-dashed border-slate-300 text-center">
                      <p className="text-sm text-slate-500">No employees added yet</p>
                      <p className="text-xs text-slate-400 mt-1">Click &quot;Add Employee&quot; to create one</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {employees.map((emp) => (
                        <div 
                          key={emp.id}
                          data-testid={`employee-row-${emp.id}`}
                          className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="font-bold text-sm" style={{ color: "#0066cc" }}>
                                {emp.name?.charAt(0)?.toUpperCase() || "E"}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{emp.name}</p>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px]">{emp.role || "Cashier"}</Badge>
                                <span className="text-xs text-slate-400">PIN: ••••</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              data-testid={`edit-employee-${emp.id}`}
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={() => openEmployeeDialog("edit", emp)}
                            >
                              <Pencil className="h-4 w-4 text-slate-500" />
                            </Button>
                            <Button 
                              data-testid={`delete-employee-${emp.id}`}
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={() => handleDeleteEmployee(emp)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className={`p-3 rounded-xl border ${instoreEnabled ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
                  <p className={`text-xs ${instoreEnabled ? "text-blue-700" : "text-slate-500"}`}>
                    <strong>Access:</strong> {instoreEnabled 
                      ? `In-Store Mode enabled at /employee (${employees.length} employee${employees.length !== 1 ? "s" : ""} registered)` 
                      : "In-Store Mode disabled"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Concept Operating Hours */}
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <Clock className="h-5 w-5" style={{ color: BRAND_COLORS.deepTeal }} />
                  Concept Operating Hours
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-500">Set operating hours for each food concept. These hours control when items are available to customers.</p>
                
                {["chicken", "pizza", "burgers"].map((conceptKey) => {
                  const isExpanded = expandedConcepts[conceptKey];
                  const schedule = conceptSchedules[conceptKey] || {};
                  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
                  
                  return (
                    <div key={conceptKey} className="border rounded-xl overflow-hidden">
                      <button
                        data-testid={`expand-schedule-${conceptKey}`}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        onClick={() => setExpandedConcepts(prev => ({ ...prev, [conceptKey]: !prev[conceptKey] }))}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CONCEPT_COLORS[conceptKey] }} />
                          <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>
                            {CONCEPT_LABELS[conceptKey]}
                          </span>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                      
                      {isExpanded && (
                        <div className="border-t bg-slate-50/50 p-4 space-y-3">
                          <div className="grid gap-2">
                            {days.map((day) => {
                              const daySchedule = schedule[day] || { enabled: true, open: "05:00", close: "21:00" };
                              return (
                                <div 
                                  key={day} 
                                  className={`flex items-center gap-3 p-2 rounded-lg ${daySchedule.enabled ? "bg-white" : "bg-slate-100"}`}
                                >
                                  <Switch
                                    data-testid={`schedule-${conceptKey}-${day}-enabled`}
                                    checked={daySchedule.enabled}
                                    onCheckedChange={(checked) => handleScheduleChange(conceptKey, day, "enabled", checked)}
                                    className="scale-75"
                                  />
                                  <span className="w-20 text-sm font-medium capitalize" style={{ color: daySchedule.enabled ? BRAND_COLORS.darkSlate : "#999" }}>
                                    {day.slice(0, 3)}
                                  </span>
                                  <Input
                                    data-testid={`schedule-${conceptKey}-${day}-open`}
                                    type="time"
                                    value={daySchedule.open}
                                    onChange={(e) => handleScheduleChange(conceptKey, day, "open", e.target.value)}
                                    disabled={!daySchedule.enabled}
                                    className="w-28 h-8 text-xs"
                                  />
                                  <span className="text-xs text-slate-400">to</span>
                                  <Input
                                    data-testid={`schedule-${conceptKey}-${day}-close`}
                                    type="time"
                                    value={daySchedule.close}
                                    onChange={(e) => handleScheduleChange(conceptKey, day, "close", e.target.value)}
                                    disabled={!daySchedule.enabled}
                                    className="w-28 h-8 text-xs"
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <Button
                            data-testid={`save-schedule-${conceptKey}`}
                            size="sm"
                            className="w-full h-9 text-white"
                            style={{ backgroundColor: CONCEPT_COLORS[conceptKey] }}
                            onClick={() => handleSaveSchedule(conceptKey)}
                            disabled={scheduleSaving[conceptKey]}
                          >
                            {scheduleSaving[conceptKey] ? "Saving..." : `Save ${CONCEPT_LABELS[conceptKey]} Schedule`}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Save All Settings Button */}
            <div className="flex justify-end">
              <Button 
                className="h-12 px-8 rounded-xl font-bold text-white"
                style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
                onClick={handleSaveRestaurantSettings}
                disabled={settingsSaving}
              >
                {settingsSaving ? "Saving..." : "Save All Settings"}
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Simplified Footer */}
      <Footer simplified />

      {/* Item Dialog */}
      <Dialog open={itemDialog.open} onOpenChange={(o) => !o && setItemDialog({ open: false, mode: "create", item: null })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>
              {itemDialog.mode === "create" ? "Add Menu Item" : "Edit Menu Item"}
            </DialogTitle>
            <DialogDescription>
              {itemDialog.mode === "create" ? "Create a new menu item (saved as draft)" : "Edit menu item details"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Concept *</Label>
                <Select value={itemForm.concept} onValueChange={(v) => setItemForm(p => ({ ...p, concept: v, category: "" }))}>
                  <SelectTrigger className="mt-1" data-testid="item-concept-select">
                    <SelectValue placeholder="Select concept" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONCEPT_OPTIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Category *</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1" data-testid="item-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {getCategoriesForConcept(itemForm.concept).map(c => (
                      <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Name *</Label>
                <Input
                  data-testid="item-name-input"
                  className="mt-1"
                  value={itemForm.name}
                  onChange={(e) => setItemForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Chicken Pieces"
                />
              </div>
              <div>
                <Label className="text-sm">Size</Label>
                <Input
                  data-testid="item-size-input"
                  className="mt-1"
                  value={itemForm.size}
                  onChange={(e) => setItemForm(p => ({ ...p, size: e.target.value }))}
                  placeholder="e.g. 2pc, 1/4 lb"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm">Price *</Label>
              <Input
                data-testid="item-price-input"
                type="number"
                step="0.01"
                className="mt-1"
                value={itemForm.price}
                onChange={(e) => setItemForm(p => ({ ...p, price: e.target.value }))}
                placeholder="e.g. 9.99"
              />
            </div>

            <div>
              <Label className="text-sm">Description</Label>
              <Textarea
                data-testid="item-description-input"
                className="mt-1"
                value={itemForm.description}
                onChange={(e) => setItemForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
                rows={2}
              />
            </div>

            {/* Image Upload */}
            <div>
              <Label className="text-sm">Image</Label>
              <div className="mt-1 flex items-center gap-3">
                {itemForm.image ? (
                  <div className="relative">
                    <img src={itemForm.image} alt="Preview" className="w-20 h-20 rounded-lg object-cover" />
                    <button
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      onClick={() => setItemForm(p => ({ ...p, image: "" }))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-slate-400" />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="upload-image-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" /> Upload
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Max 2MB. Stored in database.</p>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-900">Requires Piece Selection</p>
                <p className="text-xs text-slate-500">Customer must select specific pieces (Wing, Thigh, etc.)</p>
              </div>
              <Switch
                data-testid="piece-selection-toggle"
                checked={itemForm.requires_piece_selection}
                onCheckedChange={(v) => setItemForm(p => ({ ...p, requires_piece_selection: v }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-900">Available</p>
                <p className="text-xs text-slate-500">Item is available for ordering</p>
              </div>
              <Switch
                data-testid="item-available-toggle"
                checked={itemForm.available}
                onCheckedChange={(v) => setItemForm(p => ({ ...p, available: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog({ open: false, mode: "create", item: null })}>
              Cancel
            </Button>
            <Button data-testid="save-item-btn" style={{ backgroundColor: "#FF6347" }} onClick={handleSaveItem}>
              {itemDialog.mode === "create" ? "Create Item" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onOpenChange={(o) => !o && setCategoryDialog({ open: false, mode: "create", category: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>
              {categoryDialog.mode === "create" ? "Add Category" : "Edit Category"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Concept *</Label>
              <Select value={categoryForm.concept} onValueChange={(v) => setCategoryForm(p => ({ ...p, concept: v }))}>
                <SelectTrigger className="mt-1" data-testid="cat-concept-select">
                  <SelectValue placeholder="Select concept" />
                </SelectTrigger>
                <SelectContent>
                  {CONCEPT_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm">Display Name *</Label>
              <Input
                data-testid="cat-name-input"
                className="mt-1"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Family Meals - Chicken"
              />
            </div>

            <div>
              <Label className="text-sm">Key (unique identifier) *</Label>
              <Input
                data-testid="cat-key-input"
                className="mt-1 font-mono"
                value={categoryForm.key}
                onChange={(e) => setCategoryForm(p => ({ ...p, key: e.target.value }))}
                placeholder="e.g. family_meal"
              />
              <p className="text-xs text-slate-500 mt-1">Lowercase, underscores for spaces</p>
            </div>

            <div>
              <Label className="text-sm">Sort Order</Label>
              <Input
                data-testid="cat-sort-input"
                type="number"
                className="mt-1"
                value={categoryForm.sort_order}
                onChange={(e) => setCategoryForm(p => ({ ...p, sort_order: e.target.value }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-900">Published</p>
                <p className="text-xs text-slate-500">Category visible on customer menu</p>
              </div>
              <Switch
                data-testid="cat-published-toggle"
                checked={categoryForm.published}
                onCheckedChange={(v) => setCategoryForm(p => ({ ...p, published: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog({ open: false, mode: "create", category: null })}>
              Cancel
            </Button>
            <Button data-testid="save-category-btn" style={{ backgroundColor: "#FF6347" }} onClick={handleSaveCategory}>
              {categoryDialog.mode === "create" ? "Create Category" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, type: null, id: null, name: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDialog.type === "item" ? "Menu Item" : "Category"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteDialog.type === "item" ? handleDeleteItem() : handleDeleteCategory()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inventory Item Dialog */}
      <Dialog open={inventoryDialog.open} onOpenChange={(o) => !o && setInventoryDialog({ open: false, type: null, mode: "create", item: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>
              {inventoryDialog.mode === "create" ? "Add" : "Edit"} {
                inventoryDialog.type === "pizza-topping" ? "Pizza Topping" :
                inventoryDialog.type === "wing-sauce" ? "Wing Flavor" :
                inventoryDialog.type === "dipping-sauce" ? "Dipping Sauce" :
                inventoryDialog.type === "extra-side" ? "Extra Side" : "Item"
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Name *</Label>
              <Input
                data-testid="inventory-name-input"
                className="mt-1"
                value={inventoryForm.name}
                onChange={(e) => setInventoryForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Pepperoni, Buffalo, Ranch"
              />
            </div>

            {inventoryDialog.type === "pizza-topping" && (
              <div>
                <Label className="text-sm">Category *</Label>
                <Select value={inventoryForm.category} onValueChange={(v) => setInventoryForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meats">Meats</SelectItem>
                    <SelectItem value="veggies">Veggies</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {(inventoryDialog.type === "dipping-sauce" || inventoryDialog.type === "extra-side") && (
              <div>
                <Label className="text-sm">Price {inventoryDialog.type === "extra-side" ? "*" : "(extra sauce)"}</Label>
                <Input
                  data-testid="inventory-price-input"
                  type="number"
                  step="0.01"
                  className="mt-1"
                  value={inventoryForm.price}
                  onChange={(e) => setInventoryForm(p => ({ ...p, price: e.target.value }))}
                  placeholder={inventoryDialog.type === "dipping-sauce" ? "0.79" : "2.99"}
                />
              </div>
            )}

            {inventoryDialog.type === "extra-side" && (
              <div>
                <Label className="text-sm">Description</Label>
                <Input
                  data-testid="inventory-desc-input"
                  className="mt-1"
                  value={inventoryForm.description}
                  onChange={(e) => setInventoryForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </div>
            )}

            <div>
              <Label className="text-sm">Sort Order</Label>
              <Input
                data-testid="inventory-sort-input"
                type="number"
                className="mt-1"
                value={inventoryForm.sort_order}
                onChange={(e) => setInventoryForm(p => ({ ...p, sort_order: e.target.value }))}
                placeholder="0"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-900">Available</p>
                <p className="text-xs text-slate-500">Item appears on customer menu</p>
              </div>
              <Switch
                data-testid="inventory-available-toggle"
                checked={inventoryForm.available}
                onCheckedChange={(v) => setInventoryForm(p => ({ ...p, available: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInventoryDialog({ open: false, type: null, mode: "create", item: null })}>
              Cancel
            </Button>
            <Button data-testid="save-inventory-btn" style={{ backgroundColor: "#FF6347" }} onClick={handleSaveInventoryItem}>
              {inventoryDialog.mode === "create" ? "Create" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Dialog */}
      <Dialog open={employeeDialog.open} onOpenChange={(o) => !o && setEmployeeDialog({ open: false, mode: "create", employee: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>
              {employeeDialog.mode === "create" ? "Add Employee" : "Edit Employee"}
            </DialogTitle>
            <DialogDescription>
              {employeeDialog.mode === "create" 
                ? "Add a new employee with a unique 4-digit PIN"
                : "Update employee information"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Employee Name</Label>
              <Input
                data-testid="employee-form-name"
                type="text"
                className="mt-1 rounded-xl h-12"
                value={employeeForm.name}
                onChange={(e) => setEmployeeForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Enter employee name"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>4-Digit PIN</Label>
              <Input
                data-testid="employee-form-pin"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                className="mt-1 rounded-xl h-12 text-center tracking-[0.5em] text-xl font-mono"
                value={employeeForm.pin}
                onChange={(e) => setEmployeeForm(p => ({ ...p, pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                placeholder="••••"
              />
              <p className="text-xs text-slate-500 mt-1">Must be unique across all employees</p>
            </div>

            <div>
              <Label className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Role/Position</Label>
              <Select
                value={employeeForm.role}
                onValueChange={(v) => setEmployeeForm(p => ({ ...p, role: v }))}
              >
                <SelectTrigger data-testid="employee-form-role" className="mt-1 rounded-xl h-12">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cashier">Cashier</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="Supervisor">Supervisor</SelectItem>
                  <SelectItem value="Team Lead">Team Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEmployeeDialog({ open: false, mode: "create", employee: null })}
            >
              Cancel
            </Button>
            <Button 
              data-testid="save-employee-btn"
              className="text-white"
              style={{ backgroundColor: "#0066cc" }} 
              onClick={handleSaveEmployee}
              disabled={instoreSaving}
            >
              {instoreSaving ? "Saving..." : (employeeDialog.mode === "create" ? "Add Employee" : "Save Changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Price Update Dialog */}
      <Dialog open={bulkPriceDialog} onOpenChange={setBulkPriceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>
              Bulk Price Update
            </DialogTitle>
            <DialogDescription>
              Update prices for all items in a concept at once
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Select Concept</Label>
              <Select
                value={bulkPriceForm.concept}
                onValueChange={(v) => setBulkPriceForm(p => ({ ...p, concept: v }))}
              >
                <SelectTrigger data-testid="bulk-concept-select" className="mt-1 rounded-xl h-12">
                  <SelectValue placeholder="Select concept" />
                </SelectTrigger>
                <SelectContent>
                  {CONCEPT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: opt.color }} />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Action</Label>
              <Select
                value={bulkPriceForm.action}
                onValueChange={(v) => setBulkPriceForm(p => ({ ...p, action: v }))}
              >
                <SelectTrigger data-testid="bulk-action-select" className="mt-1 rounded-xl h-12">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="increase_pct">Increase by %</SelectItem>
                  <SelectItem value="decrease_pct">Decrease by %</SelectItem>
                  <SelectItem value="set_price">Set exact price $</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>
                {bulkPriceForm.action === "set_price" ? "Price ($)" : "Percentage (%)"}
              </Label>
              <Input
                data-testid="bulk-value-input"
                type="number"
                step={bulkPriceForm.action === "set_price" ? "0.01" : "1"}
                min="0"
                className="mt-1 rounded-xl h-12"
                value={bulkPriceForm.value}
                onChange={(e) => setBulkPriceForm(p => ({ ...p, value: e.target.value }))}
                placeholder={bulkPriceForm.action === "set_price" ? "9.99" : "10"}
              />
            </div>

            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                This will update <strong>{conceptGroups[bulkPriceForm.concept]?.length || 0}</strong> items in <strong>{CONCEPT_LABELS[bulkPriceForm.concept]}</strong>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPriceDialog(false)}>
              Cancel
            </Button>
            <Button 
              data-testid="apply-bulk-price-btn"
              className="text-white"
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }} 
              onClick={handleBulkPriceUpdate}
              disabled={bulkPriceSaving || !bulkPriceForm.value}
            >
              {bulkPriceSaving ? "Applying..." : "Apply Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
